// app/javascript/controllers/select_controller.js
import { Controller } from "@hotwired/stimulus"
import { bindClickOutside } from "../utils/click_outside"

// This controller provides an enhanced select element with search functionality
// It's a replacement for Select2 without jQuery dependency
export default class extends Controller {
  static targets = ["select", "dropdown", "searchInput", "selectedOption", "options"]
  static classes = ["open", "highlighted", "selected"]
  static values = {
    placeholder: { type: String, default: "Select an option..." }
  }

  connect() {
    if (this.hasSelectTarget) {
      this.setupSelect()
      console.log("Select controller connected to select element")
    } else {
      console.warn("Select controller could not find a select target")
    }
  }

  // Create the enhanced select interface
  setupSelect() {
    // Hide the original select
    this.selectTarget.classList.add("hidden")
    this.selectTarget.style.display = "none"
    
    // Create the container
    const container = document.createElement("div")
    container.classList.add("enhanced-select-container", "relative")
    
    // Create the display element
    const display = document.createElement("div")
    display.classList.add("enhanced-select-display", "cursor-pointer", "border", "rounded", "p-2", "flex", "items-center", "justify-between", "bg-white")
    display.dataset.action = "click->select#toggle"
    display.dataset.selectTarget = "selectedOption"
    
    // Set initial value
    const selectedOption = this.selectTarget.options[this.selectTarget.selectedIndex]
    display.innerHTML = `
      <span>${selectedOption ? selectedOption.textContent : this.placeholderValue}</span>
      <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    `
    
    // Create search input
    const search = document.createElement("input")
    search.type = "text"
    search.placeholder = "Search..."
    search.classList.add("enhanced-select-search", "p-2", "border-b", "w-full")
    search.dataset.action = "input->select#search"
    search.dataset.selectTarget = "searchInput"
    
    // Create dropdown
    const dropdown = document.createElement("div")
    dropdown.classList.add("enhanced-select-dropdown", "absolute", "left-0", "right-0", "mt-1", "bg-white", "border", "rounded", "shadow-lg", "z-50", "max-h-60", "overflow-y-auto", "hidden")
    dropdown.dataset.selectTarget = "dropdown"
    dropdown.appendChild(search)
    
    // Create options container
    const optionsContainer = document.createElement("div")
    optionsContainer.classList.add("enhanced-select-options")
    optionsContainer.dataset.selectTarget = "options"
    
    // Add options
    Array.from(this.selectTarget.options).forEach((option, index) => {
      if (option.value) {
        const optionEl = document.createElement("div")
        optionEl.classList.add("enhanced-select-option", "p-2", "cursor-pointer", "hover:bg-gray-100")
        optionEl.dataset.value = option.value
        optionEl.dataset.action = "click->select#selectOption"
        optionEl.textContent = option.textContent
        
        // Mark as selected if this is the current value
        if (option.selected) {
          optionEl.classList.add("font-semibold", "bg-gray-50")
        }
        
        optionsContainer.appendChild(optionEl)
      }
    })
    
    dropdown.appendChild(optionsContainer)
    
    // Assemble the components
    container.appendChild(display)
    container.appendChild(dropdown)
    
    // Add to the DOM after the original select
    this.selectTarget.parentNode.insertBefore(container, this.selectTarget.nextSibling)
    
    // Close dropdown when clicking outside
    this.unbindClickOutside = bindClickOutside(this.element, () => this.close())
  }
  
  // Toggle dropdown visibility
  toggle(event) {
    event.stopPropagation()
    
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }
  
  // Open the dropdown
  open() {
    this.dropdownTarget.classList.remove("hidden")
    this.searchInputTarget.focus()
    
    // Reset search and show all options
    this.searchInputTarget.value = ""
    this.showAllOptions()
  }
  
  // Close the dropdown
  close() {
    this.dropdownTarget.classList.add("hidden")
  }
  
  // Select an option
  selectOption(event) {
    const value = event.currentTarget.dataset.value
    const text = event.currentTarget.textContent
    
    // Update the original select
    this.selectTarget.value = value
    
    // Update the display
    this.selectedOptionTarget.querySelector("span").textContent = text
    
    // Dispatch change event
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    
    // Close the dropdown
    this.close()
  }
  
  // Search functionality
  search() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    const options = this.optionsTarget.querySelectorAll(".enhanced-select-option")
    
    options.forEach(option => {
      const text = option.textContent.toLowerCase()
      if (text.includes(searchTerm)) {
        option.style.display = ""
      } else {
        option.style.display = "none"
      }
    })
  }
  
  // Show all options (reset search)
  showAllOptions() {
    const options = this.optionsTarget.querySelectorAll(".enhanced-select-option")
    options.forEach(option => {
      option.style.display = ""
    })
  }
  
  // Clean up event listener on disconnect
  disconnect() {
    if (this.unbindClickOutside) this.unbindClickOutside()
  }
}